import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';

@Component({
  selector: 'app-inline-warning-banner',
  templateUrl: './inline-warning-banner.component.html',
  styleUrls: ['./inline-warning-banner.component.scss'],
})
export class InlineWarningBannerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() title = '';
  @Input() message = '';
  @Input() icon: string = 'warning';

  // Optional close button.
  @Input() closable = false;
  @Output() close = new EventEmitter<void>();

  // Sticky in scroll containers (e.g. mat-dialog-content).
  @Input() sticky = false;
  @Input() stickyTop = 0;

  // Optional explicit scroll root (helps in tricky containers like dialogs).
  // - undefined (default): auto-detect the nearest scrollable parent
  // - null: use viewport/window scroll
  // - HTMLElement: use that element's scroll
  @Input() scrollRoot?: HTMLElement | null;

  // Shadow shows only when pinned. If `pinned` is null, pinning is auto-detected.
  @Input() pinned: boolean | null = null;
  @Input() showPinnedShadow = false;

  // Fixed positioning support (used by some pages that compute geometry).
  @Input() fixed = false;
  @Input() elevation = false;
  @Input() topPx: number | null = null;
  @Input() leftPx: number | null = null;
  @Input() widthPx: number | null = null;

  private scrollParent: HTMLElement | Window | null = null;
  private scrollSub?: Subscription;
  private autoPinned = false;

  @HostBinding('class.inline-warning-sticky-host')
  get stickyHostClass(): boolean {
    return this.sticky && !this.fixed;
  }

  @HostBinding('style.top.px')
  get stickyHostTopPx(): number | null {
    return this.sticky && !this.fixed ? this.stickyTop : null;
  }

  get effectivePinned(): boolean {
    return this.pinned === null ? this.autoPinned : this.pinned;
  }

  @HostBinding('class.is-pinned')
  get pinnedHostClass(): boolean {
    return this.sticky && !this.fixed && this.effectivePinned;
  }

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.bindScrollDetection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sticky'] || changes['stickyTop'] || changes['fixed'] || changes['scrollRoot']) {
      this.bindScrollDetection();
    }
  }

  ngOnDestroy(): void {
    this.scrollSub?.unsubscribe();
  }

  private bindScrollDetection(): void {
    this.scrollSub?.unsubscribe();
    this.scrollSub = undefined;
    this.scrollParent = null;
    this.autoPinned = false;

    if (!this.sticky || this.fixed || this.pinned !== null) {
      return;
    }

    const host = this.elementRef.nativeElement;

    // If scrollRoot is explicitly set, respect it; otherwise auto-detect.
    if (this.scrollRoot === undefined) {
      this.scrollParent = this.findScrollParent(host);
    } else {
      this.scrollParent = this.scrollRoot ?? window;
    }

    const scroll$ =
      this.scrollParent === window
        ? fromEvent(window, 'scroll')
        : fromEvent(this.scrollParent as HTMLElement, 'scroll');

    const resize$ = fromEvent(window, 'resize');

    this.scrollSub = merge(scroll$, resize$)
      .pipe(auditTime(16))
      .subscribe(() => this.updateAutoPinned());

    // Initial computation after render.
    setTimeout(() => this.updateAutoPinned());
  }

  private updateAutoPinned(): void {
    if (!this.sticky || this.fixed || this.pinned !== null) {
      this.autoPinned = false;
      return;
    }

    const scrollTop = this.getScrollTop();
    if (scrollTop <= 0) {
      this.autoPinned = false;
      return;
    }

    const host = this.elementRef.nativeElement;
    const hostRect = host.getBoundingClientRect();

    let expectedTop = this.stickyTop;
    if (this.scrollParent && this.scrollParent !== window) {
      const parentRect = (this.scrollParent as HTMLElement).getBoundingClientRect();
      expectedTop = parentRect.top + this.stickyTop;
    }

    const epsilon = 1;
    // Consider pinned only once the element has actually reached the sticky threshold.
    this.autoPinned = hostRect.top <= expectedTop + epsilon;
  }

  private getScrollTop(): number {
    if (!this.scrollParent || this.scrollParent === window) {
      return (
        window.scrollY ||
        document.documentElement.scrollTop ||
        (document.body as any)?.scrollTop ||
        0
      );
    }
    return (this.scrollParent as HTMLElement).scrollTop || 0;
  }

  private findScrollParent(startEl: HTMLElement): HTMLElement | Window {
    let el: HTMLElement | null = startEl.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
      el = el.parentElement;
    }
    return window;
  }

  onCloseClicked(): void {
    this.close.emit();
  }
}
